import { listQuotes, listReps, listStores } from '../views/sales/sales-quotes-api';
import { campaignsDashboardOnly } from '../functions/tenant-features';
import ServiceFactory from '../service';
import account from '../data/account';
import { matchDestinations } from './destinations';
const DEFAULT_TOP = 5;
function odata(base, where, term, top) {
  return `${base}?$where=${encodeURIComponent(where)}&$params=${encodeURIComponent(term)}&$top=${top || DEFAULT_TOP}&$count=true`;
}

// Browser-filtered sources (reps, stores, reports) fetch their whole list once
// and keep it in module scope per tenant for `ttl` ms — typing doesn't refetch.
const _lists = {};
function cachedList(key, ttl, fetcher) {
  const k = `${key}:${(account.get() || {}).tenantId || ''}`;
  const hit = _lists[k];
  if (hit && Date.now() - hit.t < ttl) return hit.p;
  const p = Promise.resolve().then(fetcher).then(rows => rows || []);
  _lists[k] = {
    t: Date.now(),
    p
  };
  p.catch(() => {
    delete _lists[k];
  });
  return p;
}
function contains(term, ...values) {
  const t = String(term || '').toLowerCase();
  return values.some(v => v != null && String(v).toLowerCase().includes(t));
}
function pick(o, ...keys) {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
}
function currentMonthRange() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
  return {
    start: `${y}-${m}-01`,
    end: `${y}-${m}-${String(lastDay).padStart(2, '0')}`
  };
}
function currentMonthLabel() {
  const d = new Date();
  const mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = mons[d.getMonth()];
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${m} 1 - ${m} ${lastDay}`;
}
export const REGISTRY = {
  // "Go to" — in-app pages, matched in the browser (destinations.js); no fetch.
  goto: {
    label: 'Go to',
    load: (term, ctx, top, access) => matchDestinations(term, access).slice(0, top),
    fields: ['label', 'keywords'],
    detailLink: row => row.path,
    displayName: row => row.label,
    displaySub: () => ''
  },
  customers: {
    label: 'Customers',
    // People redesign roster (AnalyticsService PeopleController): q matches
    // name, customer number, email, city and state.
    api: (term, ctx, top) => `/people/customers?q=${encodeURIComponent(term)}&top=${top}`,
    serviceType: 'product',
    enabled: a => a.customers,
    fields: ['name', 'customerNumber', 'city', 'state'],
    detailLink: row => `/Customers/account/${encodeURIComponent(String(row.id))}`,
    listLink: term => `/Customers?q=${encodeURIComponent(term)}`,
    displayName: row => row.name || row.customerNumber || String(row.id),
    displaySub: row => {
      const place = [row.city, row.state].filter(Boolean).join(', ');
      return [row.customerNumber ? `#${row.customerNumber}` : '', place].filter(Boolean).join(' · ');
    }
  },
  interactions: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Interactions / Conversions',
    api: (term, ctx, top) => odata('/metrics/data/all_time', 'Title.ToLower().Contains(@0)||Type.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'activity',
    unwrap: resp => resp && resp.data && resp.data.activity && resp.data.activity.items || [],
    fields: ['title', 'type'],
    detailLink: row => {
      const name = String(row.title || '').replace(/ /g, '@');
      return `/Activity/Detail/${row.type}/${name}/all_time`;
    },
    listLink: term => `/Activity/all_time?$where=Title.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.title,
    displaySub: row => row.type
  },
  interaction_detail: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Activity Details',
    api: (term, ctx, top) => {
      if (!ctx || !ctx.activityType || !ctx.activityName) return null;
      return odata(`/metrics/detail/${ctx.activityType}/${ctx.activityName}/all_time`, 'Source.ToLower().Contains(@0)||Medium.ToLower().Contains(@0)||Campaign.ToLower().Contains(@0)||Date.ToLower().Contains(@0)', term.toLowerCase(), top);
    },
    serviceType: 'activity',
    unwrap: resp => resp && resp.data && resp.data.activity && resp.data.activity.items || [],
    fields: ['date', 'source', 'medium', 'campaign'],
    detailLink: (row, ctx) => ctx && ctx.activityType && ctx.activityName ? `/Activity/Detail/${ctx.activityType}/${ctx.activityName}/all_time` : '/',
    listLink: (term, ctx) => ctx && ctx.activityType && ctx.activityName ? `/Activity/Detail/${ctx.activityType}/${ctx.activityName}/all_time` : '/',
    displayName: row => row.date || row.title,
    displaySub: row => [row.source, row.medium, row.campaign].filter(Boolean).join(' / ') || String(row.total || '')
  },
  interaction_keyvalues: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: () => currentMonthLabel(),
    api: () => null,
    serviceType: 'activity',
    synthetic: true,
    fields: ['title'],
    detailLink: row => {
      const ctx = row._ctx || {};
      if (!ctx.activityType || !ctx.activityName) return '/';
      const {
        start,
        end
      } = currentMonthRange();
      return `/Activity/KeyValues/${ctx.activityType}/${ctx.activityName}/${start}/${end}`;
    },
    listLink: (term, ctx) => {
      if (!ctx || !ctx.activityType || !ctx.activityName) return '/';
      const {
        start,
        end
      } = currentMonthRange();
      return `/Activity/KeyValues/${ctx.activityType}/${ctx.activityName}/${start}/${end}`;
    },
    displayName: row => `Data for "${row.title}"`,
    displaySub: row => row.type
  },
  contacts: {
    label: 'Contacts',
    // People redesign roster (AnalyticsService PeopleController): q matches
    // name, email, company and customer name. Replaces the activity-metrics
    // OData $where, which 500'd on rows with a null Name/Phone.
    api: (term, ctx, top) => `/people/contacts?q=${encodeURIComponent(term)}&top=${top}`,
    serviceType: 'product',
    enabled: a => a.contacts,
    fields: ['name', 'email', 'company'],
    detailLink: row => `/Contacts/Detail/${encodeURIComponent(String(row.id))}`,
    listLink: term => `/Contacts?q=${encodeURIComponent(term)}`,
    displayName: row => row.name || row.email || String(row.id),
    displaySub: row => [row.email, row.company].filter(Boolean).join(' · ')
  },
  products: {
    label: 'Products',
    // Match the term anywhere in the title (was StartsWith, which only
    // surfaced products literally named e.g. "SOFA"); Id stays prefix for
    // SKU search. This is the single consolidated Products group.
    api: (term, ctx, top) => odata('/product', 'Title.ToLower().Contains(@0)||Id.ToLower().StartsWith(@0)', term.toLowerCase(), top),
    serviceType: 'product',
    fields: ['title', 'id'],
    // Open the product-detail page (KPIs, trend, forecast, contacts) —
    // model-neutral, works for retail + manufacturer. (Was the Manage-Data
    // product view.)
    detailLink: row => `/Products/this_year/product/${encodeURIComponent(String(row.id))}`,
    listLink: term => `/ProductPerformance?q=${encodeURIComponent(term)}`,
    displayName: row => row.title || row.id,
    displaySub: row => row.id || ''
  },
  pages: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Pages',
    api: (term, ctx, top) => odata('/metrics/page/all_time', 'Title.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'activity',
    fields: ['title', 'url'],
    detailLink: row => `/Activity/Pages/this_month?$where=Url.ToLower()==@0&$params=${encodeURIComponent(String(row.url).toLowerCase())}`,
    listLink: term => `/Activity/Pages/this_month?$where=Title.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.title || row.url,
    displaySub: row => row.url || ''
  },
  audiences: {
    label: 'Audiences',
    api: (term, ctx, top) => odata('/audience', 'Name.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'audience',
    enabled: a => a.contacts,
    fields: ['name'],
    // The Audiences detail page (was the legacy /Audience filter grid).
    detailLink: row => `/Audiences/Detail/${encodeURIComponent(String(row.id))}/this_month`,
    displayName: row => row.name,
    displaySub: () => ''
  },
  campaigns: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Campaigns',
    // The ad-campaign list the /Campaigns surface reads (CampaignService
    // AdCampaignController); q filters on name server-side and answers
    // { totalCount, rows, totalMetrics }.
    api: (term, ctx, top) => `/campaign/ad/campaigns?q=${encodeURIComponent(term)}&$top=${top}&range=this_year`,
    serviceType: 'product',
    enabled: a => a.campaigns,
    unwrap: resp => resp && pick(resp, 'rows', 'Rows', 'campaigns', 'Campaigns') || [],
    fields: ['name'],
    // Dashboard-only (managed) tenants can't open campaign details — the route redirects there anyway.
    detailLink: row => campaignsDashboardOnly() ? '/Campaigns' : `/Campaigns/detail/${encodeURIComponent(String(row.id))}`,
    listLink: term => `/Campaigns?q=${encodeURIComponent(term)}`,
    displayName: row => String(row.name || '').replace(/_/g, ' '),
    displaySub: row => [row.type, row.status].filter(Boolean).join(' · ')
  },
  content_images: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Images',
    api: (term, ctx, top) => `/content/images?q=${encodeURIComponent(term)}&$top=${top}`,
    serviceType: 'product',
    enabled: a => a.contentHub,
    fields: ['title'],
    detailLink: row => `/Content/images/${encodeURIComponent(String(row.id))}`,
    displayName: row => row.title || `Image ${row.id}`,
    displaySub: row => row.product && row.product.title || row.creativeCategory || ''
  },
  reps: {
    section: 'sales',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Reps',
    // The rep roster is small: fetched once (10 min) and filtered here.
    load: (term, ctx, top) => cachedList('reps', 10 * 60 * 1000, listReps).then(rows => rows.filter(r => contains(term, r.name, r.repCode, r.email)).slice(0, top)),
    enabled: a => a.territory,
    fields: ['name', 'repCode', 'email'],
    detailLink: row => `/Territory/this_year/rep/${encodeURIComponent(row.repCode)}`,
    displayName: row => row.name || row.repCode,
    displaySub: row => [row.repCode, row.email].filter(Boolean).join(' · ')
  },
  quotes: {
    section: 'sales',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Quotes',
    // listQuotes parses ids with json-bigint (they are 19-digit) and normalises the rows.
    load: (term, ctx, top) => listQuotes({
      q: term,
      $top: top
    }),
    enabled: a => a.quotes,
    fields: ['number', 'customerName', 'customerNumber'],
    detailLink: row => `/Quotes/${encodeURIComponent(row.id)}`,
    displayName: row => [row.number, row.customerName].filter(Boolean).join(' · ') || row.id,
    displaySub: row => row.status || ''
  },
  stores: {
    section: 'sales',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Stores',
    load: (term, ctx, top) => cachedList('stores', 10 * 60 * 1000, listStores).then(rows => rows.filter(r => contains(term, r.name, r.storeId, r.manager)).slice(0, top)),
    enabled: a => a.stores,
    fields: ['name', 'storeId', 'manager'],
    detailLink: row => `/Stores/this_year/store/${encodeURIComponent(row.storeId)}`,
    displayName: row => row.name || row.storeId,
    displaySub: row => row.manager || ''
  },
  reports: {
    label: 'Reports',
    // Saved reports this member can see (shared or theirs, ≤200): fetched
    // once (2 min — new reports show up soon) and filtered here.
    load: (term, ctx, top) => cachedList('reports', 2 * 60 * 1000, async () => {
      const Service = ServiceFactory.getType('/reports', 'product');
      const d = await new Service().getAsync();
      return d && d.items || [];
    }).then(rows => rows.filter(r => contains(term, r.name, r.description)).slice(0, top)),
    enabled: a => a.reports,
    fields: ['name', 'description'],
    detailLink: row => `/Reports/view/${encodeURIComponent(String(row.id))}`,
    displayName: row => row.name,
    displaySub: row => row.description || ''
  },
  locations: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Locations',
    api: (term, ctx, top) => odata('/metrics/location/all_time/city', 'City.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'activity',
    fields: ['city', 'postalCode', 'regionCode'],
    detailLink: row => `/Activity/Location/this_month/prev_year/city?$where=City.ToLower()==@0&$params=${encodeURIComponent(String(row.city || '').toLowerCase())}`,
    listLink: term => `/Activity/Location/this_month/prev_year/city?$where=City.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => [row.city, row.regionCode].filter(Boolean).join(', '),
    displaySub: row => row.postalCode || ''
  },
  product_location: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Product Locations',
    api: (term, ctx, top) => odata('/metrics/product-location/all_time/city', 'City.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'activity',
    fields: ['city', 'postalCode', 'regionCode'],
    detailLink: row => `/Activity/Product/Location/this_month/prev_year/city?$where=City.ToLower()==@0&$params=${encodeURIComponent(String(row.city || '').toLowerCase())}`,
    listLink: term => `/Activity/Product/Location/this_month/prev_year/city?$where=City.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => [row.city, row.regionCode].filter(Boolean).join(', '),
    displaySub: row => row.postalCode || ''
  },
  activity_utm: {
    section: 'marketing',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'UTM Activity',
    api: (term, ctx, top) => odata('/metrics/utm/all_time', `Source.ToLower().Contains(@0)||Medium.ToLower().Contains(@0)||Campaign.ToLower().Contains(@0)`, term.toLowerCase(), top),
    serviceType: 'activity',
    fields: ['source', 'medium', 'campaign'],
    detailLink: row => `/Activity/Utm/all_time`,
    listLink: term => `/Activity/Utm/all_time?$where=Campaign.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.campaign || row.source || row.medium,
    displaySub: row => [row.source, row.medium].filter(Boolean).join(' / ')
  },
  forecast_product: {
    label: 'Product Performance',
    api: (term, ctx, top) => odata('/forecast/product-forecast', 'Title.ToLower().Contains(@0)||ProductId.ToLower().StartsWith(@0)', term.toLowerCase(), top),
    serviceType: 'forecast',
    fields: ['title', 'productId'],
    detailLink: row => `/Forecast/Product/Detail/${encodeURIComponent(row.id)}`,
    listLink: term => `/Forecast/Product?$where=Title.ToLower().Contains(@0)||ProductId.ToLower().StartsWith(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.title || row.productId,
    displaySub: row => row.productId || ''
  },
  forecast_similarity: {
    section: 'sales',
    // Commerce section: hidden for members without it (WORKSPACE_SECTION_ROLES.md)
    label: 'Similarity Forecasts',
    api: (term, ctx, top) => odata('/forecast/product-similarity-forecast', 'Title.ToLower().Contains(@0)||ProductId.ToLower().StartsWith(@0)', term.toLowerCase(), top),
    serviceType: 'forecast',
    fields: ['title', 'productId'],
    detailLink: row => `/Forecast/Similarity/Detail/${encodeURIComponent(row.id)}`,
    listLink: term => `/Forecast/Similarity?$where=Title.ToLower().Contains(@0)||ProductId.ToLower().StartsWith(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.title || row.productId,
    displaySub: row => row.productId || ''
  },
  company_analysis: {
    label: 'Company Analysis',
    api: (term, ctx, top) => odata('/company-analysis', 'CompanyName.ToLower().Contains(@0)||Domain.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'research',
    fields: ['companyName', 'domain'],
    detailLink: row => `/Research/Company/Detail/${encodeURIComponent(row.id || row.domain)}`,
    listLink: term => `/Research/Company?$where=CompanyName.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.companyName || row.domain,
    displaySub: row => row.domain || ''
  },
  market_trends: {
    label: 'Market Trends',
    api: (term, ctx, top) => odata('/market-trends', 'Topic.ToLower().Contains(@0)||Title.ToLower().Contains(@0)', term.toLowerCase(), top),
    serviceType: 'research',
    fields: ['topic', 'title'],
    detailLink: row => `/Research/Trends/Detail/${encodeURIComponent(row.id)}`,
    listLink: term => `/Research/Trends?$where=Topic.ToLower().Contains(@0)||Title.ToLower().Contains(@0)&$params=${encodeURIComponent(term.toLowerCase())}`,
    displayName: row => row.title || row.topic,
    displaySub: row => row.topic || ''
  }
};
export default REGISTRY;